/**
 * Mentology Classroom — canonical knowledge & environment indexing.
 * Bound strictly to the three Founder master specifications:
 *  1. THE MENTOR-LEARNING-Program ARCHITECTURAL MANUAL.docx
 *  2. Mentor Learning Program Technical Brief.docx (on-disk filename)
 *  3. Module_01_10.docx — active 10-module classroom curriculum (Modules 01–10)
 */

export const MASTER_SPECIFICATION_FILES = [
  {
    id: 'architectural-manual',
    fileName: 'THE MENTOR-LEARNING-Program ARCHITECTURAL MANUAL.docx',
    localPath: 'public/Assets/Founder/THE MENTOR-LEARNING-Program ARCHITECTURAL MANUAL.docx',
    governs: '11 viewports (Screen-MPL-01..11) and UI token rules',
  },
  {
    id: 'technical-brief',
    fileName: 'Mentor Learning Program Technical Brief.docx',
    localPath: 'public/Assets/Founder/Mentor Learning Program Technical Brief.docx',
    governs: 'Next.js standalone deployment, /home/mentolog/mentology-learning-app, SQLite schema',
  },
  {
    id: 'curriculum-module-01-10',
    fileName: 'Module_01_10.docx',
    localPath: 'M:\\Shared drives\\Mentology Vault\\Andre-Library\\Andre-Knowledge\\Classroom-Learning-Material\\ML_01-Mentor Learning Program\\Module_01_10.docx',
    governs: 'Active 10-module classroom curriculum (Modules 01–10)',
  },
] as const;

export const DEPLOYMENT_PATHS = {
  productionAppRoot: '/home/mentolog/mentology-learning-app',
  productionDatabase: '/home/mentolog/mentology-learning-app/db/mentology_storage.db',
  localAppRoot: 'C:\\Mentology_Classroom',
  bypassTestKeyEnv: 'BYPASS_TEST_KEY',
  bypassTestKeyDefault: 'M1-TEST-ME7',
} as const;

/** Google Drive + local mirror layout — ARCHITECTURAL MANUAL §5. */
export const VAULT_M_ROOT = 'M:\\Shared drives\\Mentology Vault';
const ANDRE_KNOWLEDGE_ROOT = `${VAULT_M_ROOT}\\Andre-Library\\Andre-Knowledge`;
const CLASSROOM_MATERIAL_ROOT = `${ANDRE_KNOWLEDGE_ROOT}\\Classroom-Learning-Material`;
const ML_PROGRAM_ROOT = `${CLASSROOM_MATERIAL_ROOT}\\ML_01-Mentor Learning Program`;
const ML_ACTIVITIES_ROOT = `${ML_PROGRAM_ROOT}\\Activities`;
const ML_VAULT_ROOT = `${CLASSROOM_MATERIAL_ROOT}\\ML-02-Mentor Learning Vault`;

export { ML_ACTIVITIES_ROOT };

/** Immutable storage schema — Structural Core Library Matrix (ARCHITECTURAL MANUAL §5). */
export const STRUCTURAL_CORE_LIBRARY_MATRIX = `(M:) [Google Drive Workspace]
└── Shared drives
    └── Mentology Vault
        ├── Mentology_Classroom
        └── Andre-Library
            └── Andre-Knowledge
                ├── Action Research Action Learning
                ├── Classroom-Learning-Material
                │   ├── ML-01-Mentor Learning Program
                │   │   └── Activities
                │   ├── ML-02-Mentor Learning Vault
                │   │   ├── Organisation_Folder
                │   │   ├── Student_Folder
                │   │   └── Archive
                │   └── ML-03-Mentor Reference material
                └── My Thesis

Local Development Mirror: C:\\Mentology_Classroom`;

export const VAULT_PARTITIONS = [
  {
    partition: 'Mentology Vault / Mentology_Classroom',
    role: 'Local development mirror and classroom runtime partition',
    localPath: `${VAULT_M_ROOT}\\Mentology_Classroom`,
    cloudPath: `${VAULT_M_ROOT}\\Mentology_Classroom`,
  },
  {
    partition: 'Andre-Knowledge',
    role: 'Semantic vault search root — ARAL, classroom material, thesis',
    localPath: ANDRE_KNOWLEDGE_ROOT,
    cloudPath: ANDRE_KNOWLEDGE_ROOT,
  },
  {
    partition: 'Action Research Action Learning',
    role: 'ARAL methodology frameworks for vector search',
    localPath: `${ANDRE_KNOWLEDGE_ROOT}\\Action Research Action Learning`,
    cloudPath: `${ANDRE_KNOWLEDGE_ROOT}\\Action Research Action Learning`,
  },
  {
    partition: 'Classroom-Learning-Material / ML-01-Mentor Learning Program',
    role: 'Live Google Drive program root — classroom curriculum anchor',
    localPath: ML_PROGRAM_ROOT,
    cloudPath: ML_PROGRAM_ROOT,
  },
  {
    partition: 'ML-01-Mentor Learning Program / Activities',
    role: 'MPL-10 forms, readings, guides, and media linked from the Activities Box',
    localPath: ML_ACTIVITIES_ROOT,
    cloudPath: ML_ACTIVITIES_ROOT,
  },
  {
    partition: 'ML-02-Mentor Learning Vault / Organisation_Folder',
    role: 'Organisation-scoped classroom records',
    localPath: `${ML_VAULT_ROOT}\\Organisation_Folder`,
    cloudPath: `${ML_VAULT_ROOT}\\Organisation_Folder`,
  },
  {
    partition: 'ML-02-Mentor Learning Vault / Student_Folder',
    role: 'Individual student silo partition',
    localPath: `${ML_VAULT_ROOT}\\Student_Folder`,
    cloudPath: `${ML_VAULT_ROOT}\\Student_Folder`,
  },
  {
    partition: 'ML-02-Mentor Learning Vault / Archive',
    role: 'Past student registry spreadsheet — MLP_Student_Archive.csv',
    localPath: `${ML_VAULT_ROOT}\\Archive`,
    cloudPath: `${ML_VAULT_ROOT}\\Archive`,
  },
  {
    partition: 'Classroom-Learning-Material / ML-03-Mentor Reference material',
    role: 'Reference manuals, guides, and certification artefacts',
    localPath: `${CLASSROOM_MATERIAL_ROOT}\\ML-03-Mentor Reference material`,
    cloudPath: `${CLASSROOM_MATERIAL_ROOT}\\ML-03-Mentor Reference material`,
  },
  {
    partition: 'My Thesis',
    role: 'Doctoral research corpus — espoused theory vs theory-in-use',
    localPath: `${ANDRE_KNOWLEDGE_ROOT}\\My Thesis`,
    cloudPath: `${ANDRE_KNOWLEDGE_ROOT}\\My Thesis`,
  },
] as const;

/** Unified research brain mapping — Knowledge Hub (5 definitive rows, no repetition). */
export const KNOWLEDGE_ENGINE_ANCHORS = [
  {
    id: 'architectural-manual',
    anchorFile: 'Architectural Manual',
    governsContextMap: '11 viewports (Screen-MPL-01 to 11) and UI design token rules.',
    localCloudPath: 'public/Assets/Founder/THE MENTOR-LEARNING-Program ARCHITECTURAL MANUAL.docx',
    status: 'Active / Indexed',
  },
  {
    id: 'technical-brief',
    anchorFile: 'Technical Brief',
    governsContextMap: 'Next.js deployment configuration, SQLite schema, and production server environments.',
    localCloudPath: 'public/Assets/Founder/Mentor Learning Program Technical Brief.docx',
    status: 'Active / Indexed',
  },
  {
    id: 'active-module-curriculum',
    anchorFile: 'Active Module Curriculum',
    governsContextMap: 'Active 10-module classroom curriculum (Modules 01–10) used for Socratic context injection.',
    localCloudPath: `${ML_PROGRAM_ROOT}\\Module_01_10.docx`,
    status: 'Active / Indexed',
  },
  {
    id: 'classroom-learning-material',
    anchorFile: 'Classroom Learning Material',
    governsContextMap: 'Module readings, student activities, and core thematic prompt packages.',
    localCloudPath: `${VAULT_M_ROOT}\\Andre-Library\\Andre-Knowledge\\Classroom-Learning-Material\\`,
    status: 'Active / Indexed',
  },
  {
    id: 'andre-knowledge-library',
    anchorFile: 'Andre-Knowledge / Library',
    governsContextMap: 'The Learning Partner Brain: 1. Action Research Action Learning (ARAL methodology frameworks) & 2. My Thesis (Doctoral research corpus mapping espoused theory vs theory-in-use).',
    localCloudPath: `${VAULT_M_ROOT}\\Andre-Library\\Andre-Knowledge\\`,
    status: 'Active / Indexed',
  },
] as const;

/** Ten-module curriculum — Module_01_10.docx (Mentology Vault / production extract). */
export const LEARNING_MODULES = [
  { moduleId: '01', title: 'Welcome and Orientation', stage: 'Establishing' },
  { moduleId: '02', title: 'Defining Mentoring & The Mentology Framework', stage: 'Knowledge Acquisition' },
  { moduleId: '03', title: 'The Mentor Self-Assessment & Roadmap', stage: 'Knowledge Acquisition' },
  { moduleId: '04', title: 'Operational Architecture & The Alliance', stage: 'Negotiating' },
  { moduleId: '05', title: 'The Socratic Switch (Stage 2: Negotiating)', stage: 'Negotiating' },
  { moduleId: '06', title: 'Modern Mentoring & Ethics', stage: 'Development & Review' },
  { moduleId: '07', title: 'The Reflective Practitioner', stage: 'Development & Review' },
  { moduleId: '08', title: 'Peer Mentor Support & Accountability', stage: 'Development & Review' },
  { moduleId: '09', title: 'Review, Evolution & Sign-Off', stage: 'Closing / Redefinition' },
  { moduleId: '10', title: 'Your Mentoring Legacy & The Seal of Mastery', stage: 'Closing / Redefinition' },
] as const;

export const SEMANTIC_SEARCH_SCOPES = [
  'andre_knowledge',
  'classroom_learning_material',
  'classroom_activities',
  'my_thesis',
  'active_module',
  'ml_reference_material',
] as const;
